import type { QueryKey } from "@tanstack/react-query";

export type CacheEntry = readonly [QueryKey, unknown];

/**
 * The record as the cache holds it before a change and as it will be after it,
 * resolved once before any slot writes. `holders` are the filters, minus the
 * patchable ones, of every list that held the record.
 */
export interface RecordChange<R> {
  readonly before: R | undefined;
  readonly after: R | undefined;
  readonly holders: readonly Readonly<Record<string, unknown>>[];
  read(prefix: QueryKey): readonly CacheEntry[];
}

/**
 * One place an entity lives in the cache, under one key prefix. Operations take
 * every cached entry under the prefix and return only the entries they changed,
 * so callers never rebuild exact keys.
 */
export interface CacheSlot<TId, TPatch, R = unknown> {
  readonly prefix: QueryKey;
  patchAll(
    entries: readonly CacheEntry[],
    id: TId,
    patch: TPatch,
    change: RecordChange<R>,
  ): CacheEntry[];
  removeAll(entries: readonly CacheEntry[], id: TId, change: RecordChange<R>): CacheEntry[];
  find?(entries: readonly CacheEntry[], id: TId): R | undefined;
  holders?(entries: readonly CacheEntry[], id: TId): readonly Readonly<Record<string, unknown>>[];
}

/** How records sit inside one page of a list response. */
export interface PageShape<P, R, TId> {
  isPage: (value: unknown) => value is P;
  records: (page: P) => readonly R[];
  withRecords: (page: P, records: R[], countDelta: number) => P;
  idOf: (record: R) => TId;
}

export type Merge<R, TPatch> = (record: R, patch: TPatch) => R;

export function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

interface Paged {
  pages: unknown[];
  pageParams: unknown[];
}

function isPaged(value: unknown): value is Paged {
  return isObject(value) && Array.isArray(value.pages) && Array.isArray(value.pageParams);
}

export function pagesOf<P>(data: unknown, isPage: (value: unknown) => value is P): P[] | null {
  if (isPaged(data)) return data.pages.every(isPage) ? data.pages : null;
  return isPage(data) ? [data] : null;
}

export function withPages<P>(data: unknown, pages: P[]): unknown {
  return isPaged(data) ? { ...data, pages } : pages[0];
}

export function contains<P, R, TId>(
  shape: PageShape<P, R, TId>,
  pages: readonly P[],
  id: TId,
): boolean {
  return pages.some((page) => shape.records(page).some((record) => shape.idOf(record) === id));
}

export function mapRecords<P, R, TId>(
  shape: PageShape<P, R, TId>,
  pages: readonly P[],
  update: (record: R) => R,
): P[] | null {
  let changed = false;
  const next = pages.map((page) => {
    const records = shape.records(page);
    const updated = records.map(update);
    if (updated.every((record, index) => record === records[index])) return page;
    changed = true;
    return shape.withRecords(page, updated, 0);
  });
  return changed ? next : null;
}

export function dropRecord<P, R, TId>(
  shape: PageShape<P, R, TId>,
  pages: readonly P[],
  id: TId,
): P[] {
  return pages.map((page) =>
    shape.withRecords(
      page,
      shape.records(page).filter((record) => shape.idOf(record) !== id),
      -1,
    ),
  );
}

export function patchPages<P, R, TId, TPatch>(
  shape: PageShape<P, R, TId>,
  merge: Merge<R, TPatch> | undefined,
  entries: readonly CacheEntry[],
  id: TId,
  patch: TPatch,
): CacheEntry[] {
  if (!merge) return [];
  const changed: CacheEntry[] = [];
  for (const [key, data] of entries) {
    const pages = pagesOf(data, shape.isPage);
    if (!pages) continue;
    const next = mapRecords(shape, pages, (record) =>
      shape.idOf(record) === id ? merge(record, patch) : record,
    );
    if (next) changed.push([key, withPages(data, next)]);
  }
  return changed;
}

export function removeFromPages<P, R, TId>(
  shape: PageShape<P, R, TId>,
  entries: readonly CacheEntry[],
  id: TId,
): CacheEntry[] {
  const changed: CacheEntry[] = [];
  for (const [key, data] of entries) {
    const pages = pagesOf(data, shape.isPage);
    if (!pages || !contains(shape, pages, id)) continue;
    changed.push([key, withPages(data, dropRecord(shape, pages, id))]);
  }
  return changed;
}

export function findRecord<P, R, TId>(
  shape: PageShape<P, R, TId>,
  entries: readonly CacheEntry[],
  id: TId,
): R | undefined {
  for (const [, data] of entries) {
    const found = pagesOf(data, shape.isPage)
      ?.flatMap((page) => shape.records(page))
      .find((record) => shape.idOf(record) === id);
    if (found !== undefined) return found;
  }
  return undefined;
}

/** Paginated lists: infinite (`pages[]`) and single-page responses under one prefix. */
export function listSlot<P, R, TId, TPatch>(def: {
  prefix: QueryKey;
  shape: PageShape<P, R, TId>;
  merge?: Merge<R, TPatch>;
}): CacheSlot<TId, TPatch, never> {
  return {
    prefix: def.prefix,
    patchAll: (entries, id, patch) => patchPages(def.shape, def.merge, entries, id, patch),
    removeAll: (entries, id) => removeFromPages(def.shape, entries, id),
  };
}

/** Plain arrays of records (`Tag[]`, `FiscalQuota[]`). */
export function arraySlot<R, TId, TPatch>(def: {
  prefix: QueryKey;
  isRecord: (value: unknown) => value is R;
  idOf: (record: R) => TId;
  merge?: Merge<R, TPatch>;
}): CacheSlot<TId, TPatch, never> {
  const isList = (value: unknown): value is R[] =>
    Array.isArray(value) && value.every(def.isRecord);
  const shape: PageShape<R[], R, TId> = {
    isPage: isList,
    records: (page) => page,
    withRecords: (_page, records) => records,
    idOf: def.idOf,
  };
  return listSlot({ prefix: def.prefix, shape, merge: def.merge });
}

/** Single-record caches (detail queries). Removal is left to `removeQueries`. */
export function detailSlot<R, TId, TPatch>(def: {
  prefix: QueryKey;
  isRecord: (value: unknown) => value is R;
  idOf: (record: R) => TId;
  merge: Merge<R, TPatch>;
}): CacheSlot<TId, TPatch, never> {
  return {
    prefix: def.prefix,
    patchAll: (entries, id, patch) => {
      const changed: CacheEntry[] = [];
      for (const [key, data] of entries) {
        if (def.isRecord(data) && def.idOf(data) === id)
          changed.push([key, def.merge(data, patch)]);
      }
      return changed;
    },
    removeAll: () => [],
  };
}

/** Records embedded in a parent record (quotas inside a beneficiary detail). */
export function nestedSlot<Parent, R, TId, TPatch>(def: {
  prefix: QueryKey;
  isParent: (value: unknown) => value is Parent;
  children: (parent: Parent) => readonly R[];
  withChildren: (parent: Parent, children: R[]) => Parent;
  idOf: (record: R) => TId;
  merge: Merge<R, TPatch>;
}): CacheSlot<TId, TPatch, never> {
  const update = (
    entries: readonly CacheEntry[],
    id: TId,
    change: (children: readonly R[]) => R[],
  ): CacheEntry[] => {
    const changed: CacheEntry[] = [];
    for (const [key, data] of entries) {
      if (!def.isParent(data)) continue;
      const children = def.children(data);
      if (!children.some((child) => def.idOf(child) === id)) continue;
      changed.push([key, def.withChildren(data, change(children))]);
    }
    return changed;
  };
  return {
    prefix: def.prefix,
    patchAll: (entries, id, patch) =>
      update(entries, id, (children) =>
        children.map((child) => (def.idOf(child) === id ? def.merge(child, patch) : child)),
      ),
    removeAll: (entries, id) =>
      update(entries, id, (children) => children.filter((child) => def.idOf(child) !== id)),
  };
}
